import React, { useState, useMemo, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import { Button } from "src/components/ui/button";
import BorderedSelect from "src/components/form-new/bordered-select";
import ReferralsTable from "../components/referrals-table";
import ReferralDrawer from "../components/referral-drawer";
import type { Referral } from "../types/commissions.types";
import {
  useGetCommissionInvoicesQuery,
  useGetCommissionReferralsQuery,
  useGetCommissionReportsQuery,
  useCreateCommissionReferralMutation,
} from "../endpoints/commissions.endpoints";
import { useGetCompaniesQuery } from "src/redux/billing/billing.endpoints";

const PERIOD_TYPE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const label = new Date(2000, i, 1).toLocaleString("default", { month: "long" });
  return { value: String(month), label };
});

function getQuartersForPeriod(): { label: string; year: number; quarter: number }[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const quarters: { label: string; year: number; quarter: number }[] = [];
  for (let i = -2; i <= 1; i++) {
    const total = currentYear * 4 + currentQuarter + i;
    const year = Math.floor((total - 1) / 4);
    const quarter = ((total - 1) % 4) + 1;
    quarters.push({ label: `Q${quarter} ${year}`, year, quarter });
  }
  return quarters;
}

function getYearsForPeriod(): number[] {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
}

const QUARTERS = getQuartersForPeriod();
const YEARS = getYearsForPeriod();

const ReferralsView: React.FC = () => {
  const { data: commissionInvoices = [] } = useGetCommissionInvoicesQuery();
  const { data: referralsData = [] } = useGetCommissionReferralsQuery();
  const { data: companies = [] } = useGetCompaniesQuery();
  const [createReferral, { isLoading: isCreatingReferral }] =
    useCreateCommissionReferralMutation();
  const invoiceOptions = useMemo(
    () =>
      commissionInvoices.map((inv) => ({
        value: inv.id,
        label: `${inv.invoiceNumber} – ${inv.companyName}`,
      })),
    [commissionInvoices]
  );
  const companyOptions = useMemo(
    () =>
      companies.map((c) => ({
        value: c.ext_customer_id ?? c.ext_company_id ?? c.id,
        label: c.company_name ?? "",
      })),
    [companies]
  );
  const companyIdToName = useMemo(
    () =>
      companies.reduce<Record<string, string>>(
        (acc, c) => {
          const name = c.company_name ?? "";
          acc[c.id] = name;
          if (c.ext_customer_id) acc[c.ext_customer_id] = name;
          if (c.ext_company_id) acc[c.ext_company_id] = name;
          return acc;
        },
        {}
      ),
    [companies]
  );
  const companyIdToExtCustomerId = useMemo(
    () =>
      companies.reduce<Record<string, string>>(
        (acc, c) => {
          const ext = c.ext_customer_id ?? c.ext_company_id ?? c.id;
          acc[c.id] = ext;
          if (c.ext_customer_id) acc[c.ext_customer_id] = ext;
          if (c.ext_company_id) acc[c.ext_company_id] = ext;
          return acc;
        },
        {}
      ),
    [companies]
  );

  const [referrals, setReferrals] = useState<Referral[]>([]);
  const prevReferralsDataRef = useRef(referralsData);

  useEffect(() => {
    if (prevReferralsDataRef.current !== referralsData) {
      prevReferralsDataRef.current = referralsData;
      setReferrals(referralsData);
    } else if (referralsData.length > 0 && referrals.length === 0) {
      setReferrals(referralsData);
    }
  }, [referralsData, referrals.length]);

  const [periodType, setPeriodType] = useState<"month" | "quarter">("quarter");
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${q} ${y}`;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const periodLabel = useMemo(() => {
    if (periodType === "month") {
      const monthIndex = Number(selectedMonth) - 1;
      const monthName = new Date(2000, monthIndex, 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${selectedYear}`;
    }
    return selectedQuarter;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const reportParamsList = useMemo(() => {
    if (periodType === "month") {
      const month = Number(selectedMonth);
      const year = Number(selectedYear);
      const p = { type: "referral" as const, month, year };
      return [p];
    }
    const q = QUARTERS.find((x) => x.label === selectedQuarter);
    if (!q) return [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const quarterMonths = [
      { type: "referral" as const, month: (q.quarter - 1) * 3 + 1, year: q.year },
      { type: "referral" as const, month: (q.quarter - 1) * 3 + 2, year: q.year },
      { type: "referral" as const, month: (q.quarter - 1) * 3 + 3, year: q.year },
    ];
    const onlyCompletedMonths = quarterMonths.filter(
      (p) => p.year < currentYear || (p.year === currentYear && p.month < currentMonth)
    );
    if (onlyCompletedMonths.length === 0) return [];
    return onlyCompletedMonths;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const reportParams0 = reportParamsList[0];
  const reportParams1 = reportParamsList[1];
  const reportParams2 = reportParamsList[2];
  const skipReports = reportParamsList.length === 0;
  const { data: reports1 = [] } = useGetCommissionReportsQuery(reportParams0 ?? { type: "referral" }, { skip: skipReports });
  const { data: reports2 = [] } = useGetCommissionReportsQuery(reportParams1 ?? reportParams0 ?? { type: "referral" }, { skip: skipReports || reportParamsList.length < 2 });
  const { data: reports3 = [] } = useGetCommissionReportsQuery(reportParams2 ?? reportParams0 ?? { type: "referral" }, { skip: skipReports || reportParamsList.length < 3 });

  const referralIdToEstimatedCommission = useMemo(() => {
    const sources = [reports1, reports2, reports3].slice(0, reportParamsList.length);
    const all = sources.flat();
    const byReferral: Record<string, number> = {};
    for (const r of all) {
      const id = r.referral ?? r.referralId ?? "";
      if (!id) continue;
      byReferral[id] = (byReferral[id] ?? 0) + (r.commission ?? 0);
    }
    return byReferral;
  }, [reports1, reports2, reports3, reportParamsList.length]);

  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [isNewReferral, setIsNewReferral] = useState(false);

  // When editing, keep selectedReferral in sync with refetched data (e.g. after saving a line)
  useEffect(() => {
    if (!drawerOpen || !selectedReferral?.id || !referralsData.length) return;
    const updated = referralsData.find((r) => r.id === selectedReferral.id);
    if (updated && updated !== selectedReferral) setSelectedReferral(updated);
  }, [referralsData, drawerOpen, selectedReferral?.id]);

  const handleRowClick = (referral: Referral) => {
    setSelectedReferral(referral);
    setIsNewReferral(false);
    setDrawerOpen(true);
  };

  const handleAddReferral = () => {
    setSelectedReferral(null);
    setIsNewReferral(true);
    setDrawerOpen(true);
  };

  const handleSaveReferral = async (payload: Partial<Referral> & { id?: string }) => {
    if (isNewReferral) {
      const name = payload.organizationName ?? payload.name ?? "";
      const email = payload.contactEmail ?? payload.email ?? "";
      try {
        await createReferral({ name, email }).unwrap();
        setDrawerOpen(false);
        // List refetches via invalidatesTags
      } catch {
        // Error could be shown via toast
      }
    } else if (payload.id) {
      const lines = payload.referralLines ?? [];
      const firstRate = lines[0]?.rate ?? 0;
      setReferrals((prev) =>
        prev.map((r) =>
          r.id === payload.id
            ? {
                ...r,
                name: payload.name ?? r.name,
                email: payload.email ?? r.email,
                referralLines: lines.length ? lines : r.referralLines,
                organizationName: payload.organizationName ?? payload.name ?? r.organizationName,
                contactEmail: payload.contactEmail ?? payload.email ?? r.contactEmail,
                initialRatePercent: payload.initialRatePercent ?? firstRate ?? r.initialRatePercent,
                recurringRatePercent: payload.recurringRatePercent ?? firstRate ?? r.recurringRatePercent,
              }
            : r
        )
      );
    }
  };

  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <BorderedSelect
            label="View by"
            options={PERIOD_TYPE_OPTIONS}
            value={periodType}
            onChange={(e) =>
              setPeriodType(e.target.value as "month" | "quarter")
            }
          />
          {periodType === "month" ? (
            <>
              <BorderedSelect
                label="Month"
                options={MONTH_OPTIONS}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
              <BorderedSelect
                label="Year"
                options={YEARS.map((y) => ({
                  value: String(y),
                  label: String(y),
                }))}
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              />
            </>
          ) : (
            <BorderedSelect
              label="Quarter"
              options={QUARTERS.map((q) => ({
                value: q.label,
                label: q.label,
              }))}
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
            />
          )}
        </div>
        <Button
          onClick={handleAddReferral}
          className="gap-2 bg-gray-201 text-black"
          size="med"
        >
          <Plus className="h-4 w-4" />
          Add referral
        </Button>
      </div>
      <ReferralsTable
        referrals={referrals}
        quarterLabel={periodLabel}
        onRowClick={handleRowClick}
        companyIdToName={companyIdToName}
        referralIdToEstimatedCommission={referralIdToEstimatedCommission}
      />
      <ReferralDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        referral={selectedReferral}
        isNew={isNewReferral}
        onSave={handleSaveReferral}
        invoiceOptions={invoiceOptions}
        companyOptions={companyOptions}
        companyIdToExtCustomerId={companyIdToExtCustomerId}
        commissionInvoices={commissionInvoices}
      />
    </div>
  );
};

export default ReferralsView;
